import { FC, Dispatch, SetStateAction, ChangeEvent, useState, useMemo } from 'react'
import { AboutAttorneyFormContainer } from '../defaultComponents'
import { LeftBar } from '../../basic/LeftBar'
import StandardInput from '../../basic/StandardInput'
import Button from '../../basic/Button'
import StandardSelect from '../../basic/StandardSelect'
import StepBullet from '../../basic/StepBullet'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { aboutAttorneyPosition, aboutAttorneyPrivatePractice, handleYearsOfPractice } from '../../../constants/application'
import { object, string } from 'yup'
import BarCardNumberInput from 'src/components/basic/BarCardNumberInput'



export type AboutSectionData = {
  barCardNumber?: string;
  position?: string;
  emailAddress?: string;
  yearsInPrivatePractice?: number;
}

interface AttorneyFormAboutSectionProps {
  aboutSectionData: AboutSectionData;
  setAboutSectionData: Dispatch<SetStateAction<AboutSectionData>>;
  handlePageResize: () => void;
  formInitialValidation: boolean;
  onSaveDetails: () => Promise<void>;
  isOpen: boolean;
  handleOpen: () => void;
  showErrors?: boolean;
}

const AttorneyFormAboutSection: FC<AttorneyFormAboutSectionProps> = ({
  aboutSectionData,
  setAboutSectionData,
  handlePageResize,
  formInitialValidation,
  onSaveDetails,
  isOpen,
  handleOpen,
  showErrors
}) => {
  const [isAccordion, setIsAccordion] = useState<boolean>(formInitialValidation)
  const [disabledSaveDetailsButton, setDisabledSaveDetailsButton] = useState(false)
  
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target
    setAboutSectionData(prev => ({ ...prev, [name]: value }))
  }
  
  const formContainerClasses = `form-container ${
    isAccordion ? 
    (isOpen ? 'accordion-open' : 'accordion-closed') :
    ''
  }`

  const handleSaveDetails = async () => {
    setDisabledSaveDetailsButton(true)
    await onSaveDetails()
    setIsAccordion(true)
    handlePageResize()
    setDisabledSaveDetailsButton(false)
  }
  
  const handleToggleAccordion = () => {
    handleOpen()
    handlePageResize()
  }

  const validationSchema = object({
    barCardNumber: string().trim().matches(/^\d{6}$|^\d{8}$/).required('aboutSection'),
    position: string().required('aboutSection'),
    emailAddress: string().email().required('aboutSection'),
    yearsInPrivatePractice: string().required('aboutSection'),
  })

  const isFormValid = useMemo(() => {
    try {
      validationSchema.validateSync(aboutSectionData)
      return true
    } catch(e) {
      return false
    }
  }, [aboutSectionData, validationSchema])

  const isInputIncorrect = () => {
    return showErrors && !isFormValid
  }

  return (
    <AboutAttorneyFormContainer>
      <LeftBar />
      <div className={formContainerClasses}>
        <div className='form-title'>
          <StepBullet isComplete>1</StepBullet>
          <h6>ABOUT</h6>
          <button onClick={handleToggleAccordion}>
            {isOpen ? (
              <MdKeyboardArrowUp />
            ) : (
              <MdKeyboardArrowDown />
            )}
          </button>
        </div>
        <div className='form-content'>
          <BarCardNumberInput 
            name="barCardNumber"
            onChange={handleChange}
            value={aboutSectionData?.barCardNumber ?? ''}
          />
          <StandardSelect
            label='Position'
            name="position"
            value={aboutSectionData?.position ?? ''}
            onChange={handleChange}
            isIncorrect={isInputIncorrect() && !aboutSectionData?.position}
            errorMessage={isInputIncorrect() && !aboutSectionData?.position ? 'This is a required field' : ''}
          >
            <option disabled value="">Select</option>
            {aboutAttorneyPosition.map((position, index) => (
              <option key={index} value={position}>{position}</option>
            ))}
          </StandardSelect>
          <StandardInput
            label='Email'
            type='email'
            name="emailAddress"
            value={aboutSectionData?.emailAddress ?? ''}
            onChange={handleChange}
            isIncorrect={isInputIncorrect() && !aboutSectionData?.emailAddress}
            errorMessage={isInputIncorrect() && !aboutSectionData?.emailAddress ? 'This is a required field' : ''}
          />
          <StandardSelect
            label='Years in Private Practice'
            name="yearsInPrivatePractice"
            value={handleYearsOfPractice(aboutSectionData?.yearsInPrivatePractice)}
            onChange={handleChange}
            isIncorrect={isInputIncorrect() && !aboutSectionData?.yearsInPrivatePractice}
            errorMessage={isInputIncorrect() && !aboutSectionData?.yearsInPrivatePractice ? 'This is a required field' : ''}
          >
            <option disabled value="">Select</option>
            {aboutAttorneyPrivatePractice.map((practice, index) => (
              <option key={index} value={index}>{practice}</option>
            ))}
          </StandardSelect>
          <Button 
            onClick={() => handleSaveDetails()}
            disabled={disabledSaveDetailsButton ? true : !isFormValid}
          >
            {disabledSaveDetailsButton ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </AboutAttorneyFormContainer>
  )
}

export default AttorneyFormAboutSection